import "./ShiftDetail.scss";

import { Col, Container, Form, Row } from "reactstrap";
import {
  EVENT_TYPE,
  UPLOAD_CSV_TYPE,
  acceptableFormats,
} from "../../../config/constants";
import {
  IShiftDetailPageProps,
  IShiftDetailPageState,
} from "../../../interfaces/shiftDetail.interface";
import { useEffect, useState } from "react";

import ButtonComponent from "../../common/Button/ButtonComponent";
import FormSectionLayout from "../../layouts/FormSectionLayout";
import HeadingText from "../../common/HeadingText/HeadingText";
import { IWarehousePayload } from "../../../interfaces/Index";
import LoaderComponent from "../../common/Loader/Loader";
import React from "react";
import SearchableSelect from "../../common/SearchableSelect/SearchableSelect";
import Toaster from "../../common/Toaster/Toaster";
import UploadButtonComponent from "../../common/UploadButton/UploadButtonComponent";
import ability from "../../../config/ability";
import { getWarehouseShift } from "../../../redux/sources/WarehouseOpsSource";
import { normalizeWarehouseResponseForDropdown } from "../../../normalizer/ShipperNormalizer";

export const getShiftOnSelect = async (
  fieldId: string,
  warehouse: IWarehousePayload,
  shiftState: IShiftDetailPageState,
  setShiftState: React.Dispatch<React.SetStateAction<IShiftDetailPageState>>
) => {
  const payload = await getWarehouseShift(warehouse.id);
  setShiftState({
    ...shiftState,
    [fieldId]: warehouse,
    shiftId: payload.shiftId ? payload.shiftId : null,
    isShiftStart: payload.shiftId ? true : false,
  });
};

const ShiftDetailPage: React.SFC<IShiftDetailPageProps> = (props) => {
  const headingText = "Shift details";
  const [shiftState, setShiftState] = useState<IShiftDetailPageState>({
    selectedWarehouse: props.dummyWarehouse ? props.dummyWarehouse : null,
    isShiftStart: props.dummyShiftStart ? !!props.dummyShiftStart : false,
    shiftId: props.dummyShiftStart ? props.dummyShiftStart : null,
    csvFileFormData: null,
  });

  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: "",
    isError: false,
  });

  useEffect(() => {
    const { getWarehouses, warehouseList } = props;
    if (warehouseList.length === 0) {
      getWarehouses();
    }
  }, []);

  useEffect(() => {
    const { isWarehouseShiftStarted, warehouseShiftId } = props;
    if (isWarehouseShiftStarted) {
      setShiftState({
        ...shiftState,
        shiftId: warehouseShiftId,
        isShiftStart: true,
      });
    }
  }, [props.isWarehouseShiftStarted]);

  useEffect(() => {
    if (props.isWarehouseShiftEnded) {
      const message = "Shift ended successfully!";
      handleToaster(false, message);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }, [props.isWarehouseShiftEnded]);

  useEffect(() => {
    const { isTourAdded, isTourCsvUploaded } = props;
    if (isTourAdded && isTourCsvUploaded) {
      const message = "CSV uploaded and Tour event added";
      handleToaster(false, message);
    }
  }, [props.isTourAdded, props.isTourCsvUploaded]);

  useEffect(() => {
    const { error, isError } = props;
    if (isError) {
      alert((error! as any).errors);
    }
  }, [props.isError]);

  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
    setTimeout(() => {
      setToaster({
        isVisible: false,
        message: "",
        isError: false,
      });
    }, 3100);
  };

  const handleSearchableSelect = (e: IWarehousePayload, fieldId: string) => {
    getShiftOnSelect(fieldId, e, shiftState, setShiftState);
  };

  const handleCSVInputChange = (e: any) => {
    const file = e;
    if (file) {
      const fileData = new FormData();
      fileData.append("csv", file);
      setShiftState({
        ...shiftState,
        csvFileFormData: fileData,
      });
      e = null;
    }
  };

  const handleShiftStart = () => {
    const { startWarehouseShift } = props;
    startWarehouseShift(shiftState.selectedWarehouse?.id as string);
  };

  const handleShiftEnd = () => {
    const { endWarehouseShift } = props;
    endWarehouseShift(shiftState.shiftId as string, EVENT_TYPE.ENDED);
  };

  const handleCsvUploadAndSubmit = () => {
    const { uploadTourAndUpdateShiftEvent } = props;
    uploadTourAndUpdateShiftEvent(
      shiftState.shiftId as string,
      EVENT_TYPE.TOURS_ADDED,
      UPLOAD_CSV_TYPE.TOUR,
      shiftState.csvFileFormData as FormData,
      shiftState.selectedWarehouse?.id as string
    );
  };

  const renderUploadSubmitButtons = () => {
    return (
      <div className="form-row-separator">
        <Row id="addEditUploadButtonRow">
          {(ability.can("update", "shift") ||
            ability.can("create", "shift")) && (
            <>
              <UploadButtonComponent
                id="upload-shift-csv"
                buttonText="Upload .CSV"
                fileFormat={acceptableFormats.csv}
                handleFileChange={handleCSVInputChange}
                classes="button-medium"
                isDisable={props.isLoading || !shiftState.isShiftStart}
              />
              <ButtonComponent
                buttonClass="large-button btn-no-border"
                buttonText={"Submit"}
                onClickHandler={handleCsvUploadAndSubmit}
                isDisable={
                  props.isLoading ||
                  !shiftState.isShiftStart ||
                  !shiftState.csvFileFormData
                }
              />
            </>
          )}
        </Row>
      </div>
    );
  };

  const renderShiftStartStopButtons = () => {
    return (
      <div className="form-row-separator">
        <Row id="addEditStartStopButtonRow">
          {(ability.can("update", "shift") ||
            ability.can("create", "shift")) && (
            <>
              <ButtonComponent
                buttonClass="large-button btn-primary"
                buttonText={"Start Shift"}
                onClickHandler={handleShiftStart}
                isDisable={
                  props.isLoading ||
                  props.isWarehouseListLoading ||
                  !shiftState.selectedWarehouse ||
                  !!shiftState.shiftId
                }
              />
              <ButtonComponent
                buttonClass="large-button btn-greyed"
                buttonText={"End Shift"}
                onClickHandler={handleShiftEnd}
                isDisable={props.isLoading || !shiftState.isShiftStart}
              />
            </>
          )}
        </Row>
      </div>
    );
  };

  const renderShiftDetail = () => {
    return (
      <FormSectionLayout subTitleText="Shift details">
        <div className="form-row">
          <Col>
            <SearchableSelect
              id="selectedWarehouse"
              isSearchable={true}
              options={normalizeWarehouseResponseForDropdown(
                props.warehouseList
              )}
              onChange={(e: any) => {
                handleSearchableSelect(e, "selectedWarehouse");
              }}
              label="Select warehouse"
              placeholder="Select warehouse"
              selectedValue={shiftState.selectedWarehouse}
            />
          </Col>
        </div>
        {renderUploadSubmitButtons()}
        {renderShiftStartStopButtons()}
      </FormSectionLayout>
    );
  };

  return (
    <Container fluid className="shifts-root-container">
      {(props.isLoading || props.isWarehouseListLoading) && <LoaderComponent />}
      <Toaster
        showToaster={showToaster.isVisible}
        toasterClass={
          showToaster.isError
            ? "toaster toaster-bg-danger"
            : "toaster toaster-bg-success"
        }
      >
        {showToaster.message || ""}
      </Toaster>
      <Row className="shift-detail-heading">
        <Col lg="6">
          <HeadingText text={headingText} isMainHeading={true} />
        </Col>
        {shiftState.shiftId && (
          <Col lg="6" className="progress-container">
            <div className="shift-progress-notification">
              <p>Shift in progress</p>
            </div>
          </Col>
        )}
      </Row>
      <Row className="form-container shifts-container">
        <Col xs="12">
          <Form>
            <div className="shift-details">{renderShiftDetail()}</div>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default ShiftDetailPage;
